/**
 * Orchestration view of a project (app-clarity `dl/goal`, `ide/ide-project`): the goal at the
 * root, each lane with its latest upward update (↑), then its runs, questions and next tasks.
 * Every node is real: lanes open their lane page, runs open (Take over), questions answer, a
 * blocked lane offers Fix. "Updates to the goal" lists what the lanes reported, newest first.
 */
import { Link } from '@tanstack/react-router'
import { StatusIcon } from '@viko/transcript-ui'
import { ArrowUp, Target } from 'lucide-react'
import { useMemo } from 'react'
import {
  buildGoalTree,
  type TreeChild,
  type TreeLane,
  type TreeStatus,
} from '../../lib/goal-tree.js'
import { runHref, workstreamHref } from '../../lib/ia.js'
import type { LanesModel } from '../../lib/lanes-model.js'
import { Ago } from '../ui.js'
import './goal-tree.css'

const STATUS_WORD: Record<TreeStatus, string> = {
  blocked: 'Blocked',
  wait: 'Needs you',
  run: 'Running',
  idle: 'Queued',
  ok: 'Landed',
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function GoalTree({
  model,
  goal,
  onInspect,
}: {
  model: LanesModel
  /** The goal's text (the project's goal, or its name when it has none). */
  goal: string | undefined
  onInspect: (key: string) => void
}) {
  const tree = useMemo(() => buildGoalTree(model), [model])
  const meta = [
    plural(tree.counts.lanes, 'active lane'),
    tree.counts.running ? `${tree.counts.running} running` : '',
    tree.counts.needs ? `${tree.counts.needs} need you` : '',
    tree.counts.blocked ? `${tree.counts.blocked} blocked` : '',
  ].filter(Boolean)
  return (
    <div className="gt" data-goal-tree>
      <section className="gt-tree" aria-label="Orchestration">
        <div className="gt-node gt-root">
          <span className="gt-icon" aria-hidden="true">
            <Target size={16} />
          </span>
          <div className="gt-text">
            <p className="gt-title">{goal || `${model.name}: no goal set yet`}</p>
            <p className="gt-meta">{meta.join(' · ')}</p>
          </div>
        </div>
        {tree.lanes.length ? (
          <ul className="gt-list" aria-label="Lanes">
            {tree.lanes.map((l) => (
              <LaneNode key={l.id} lane={l} model={model} onInspect={onInspect} />
            ))}
          </ul>
        ) : (
          <p className="gt-empty">No active lanes. What you ask Viko becomes lanes here.</p>
        )}
        {tree.idle.length ? (
          <details className="gt-idle">
            <summary>
              <StatusIcon kind="idle" />
              <span>{plural(tree.idle.length, 'idle lane')}</span>
            </summary>
            <ul className="gt-idle__list">
              {tree.idle.map((l) => (
                <li key={l.id}>
                  {l.pseudo ? (
                    <span>{l.name}</span>
                  ) : (
                    <Link to={workstreamHref(l.id, model.projectId)}>{l.name}</Link>
                  )}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </section>
      <aside className="gt-updates" aria-labelledby="gt-updates-h">
        <h2 id="gt-updates-h" className="gt-label">
          Updates to the goal <span className="gt-count">{tree.updates.length}</span>
        </h2>
        {tree.updates.length ? (
          <ol className="gt-ulist">
            {tree.updates.map((u) => (
              <li key={u.key}>
                <span className="gt-when">
                  <Ago ts={u.at} />
                </span>
                <button type="button" className="gt-ubtn" onClick={() => onInspect(u.open)}>
                  <b>{u.lane}</b>
                  <span>{u.text}</span>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <p className="gt-meta">Nothing reported yet.</p>
        )}
        <p className="gt-meta">
          Every node is a real lane, run or question: open it to read its transcript, files and
          diff, or take it over.
        </p>
      </aside>
    </div>
  )
}

function LaneNode({
  lane,
  model,
  onInspect,
}: {
  lane: TreeLane
  model: LanesModel
  onInspect: (key: string) => void
}) {
  const blocked = lane.status === 'blocked'
  return (
    <li className={`gt-branch is-${lane.status}`} data-lane-node={lane.id}>
      <div className="gt-node">
        <span className="gt-icon">
          <StatusIcon kind={lane.status} label={STATUS_WORD[lane.status]} />
        </span>
        <div className="gt-text">
          <p className="gt-title">{lane.name}</p>
          <p className="gt-meta gt-up">
            <ArrowUp size={14} aria-hidden="true" className="gt-up__i" />
            {blocked ? (
              <span>
                <b className="gt-blocked">Blocked</b> · {lane.blockedReason}
              </span>
            ) : (
              <span>{lane.update}</span>
            )}
          </p>
        </div>
        <span className="gt-act">
          {blocked && lane.fixKey ? (
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => onInspect(lane.fixKey as string)}
            >
              Fix
            </button>
          ) : lane.pseudo ? null : (
            <Link className="gt-link" to={workstreamHref(lane.id, model.projectId)}>
              Open
            </Link>
          )}
        </span>
      </div>
      {lane.children.length || lane.moreQueued ? (
        <ul className="gt-list gt-children">
          {lane.children.map((c) => (
            <ChildNode key={c.key} node={c} model={model} onInspect={onInspect} />
          ))}
          {lane.moreQueued ? (
            <li className="gt-more">
              {lane.pseudo ? (
                <span>{lane.moreQueued} more queued</span>
              ) : (
                <Link className="gt-link" to={workstreamHref(lane.id, model.projectId)}>
                  {lane.moreQueued} more queued
                </Link>
              )}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  )
}

function ChildNode({
  node,
  model,
  onInspect,
}: {
  node: TreeChild
  model: LanesModel
  onInspect: (key: string) => void
}) {
  return (
    <li className={`gt-branch is-${node.status}`}>
      <div className="gt-node is-child">
        <span className="gt-icon">
          <StatusIcon kind={node.status} label={STATUS_WORD[node.status]} />
        </span>
        <div className="gt-text">
          <p className="gt-title">{node.title}</p>
          <p className="gt-meta">{node.meta}</p>
        </div>
        <span className="gt-act">
          {node.runId ? (
            <Link
              className="vk-btn vk-btn--secondary vk-btn--sm"
              to={runHref(node.runId, model.projectId)}
            >
              {node.status === 'run' ? 'Take over' : 'Open'}
            </Link>
          ) : node.answerKey ? (
            <button
              type="button"
              className="gt-link"
              onClick={() => onInspect(node.answerKey as string)}
            >
              Answer
            </button>
          ) : node.taskKey ? (
            <button
              type="button"
              className="gt-link"
              onClick={() => onInspect(node.taskKey as string)}
            >
              {node.status === 'blocked' ? 'Fix' : 'Open'}
            </button>
          ) : null}
        </span>
      </div>
    </li>
  )
}
